import { getResource } from "../../../language/resources/getResource";
import { AccessRuleChangeSet } from "../models";
import { LiftAccessRuleSet, liftAccessRuleSetKey } from "./models";
import type { BulkChangeModes } from "./LiftAccessRuleSetBulkEditor";
import {
  mergeEffectiveRule,
  rulesToAdd,
} from "../requests/accessRuleBulkChangeRequestMapper";
import { blackoutDateRangeDiffAdapter } from "../blackoutDateRange/blackoutDateRange";
import { blackoutDayOfWeekDiffAdapter } from "../blackoutDayOfWeek/blackoutDayOfWeek";
import {
  MISSING,
  cleanLines,
  timeRangeText,
  formatDateValue,
  maxDaysLabel,
  blackoutRangeLabel,
  blackoutDowGroupedLabels,
  effectiveStartLines,
  effectiveEndLines,
  effectiveTimeLines,
  maxDaysLines,
  blackoutRangeLines,
  blackoutDowLines,
} from "./ruleSetDisplay";

type CategoryKey = keyof BulkChangeModes;

type Resources = Record<string, any>;

type CellCtx = {
  dateFormat: string;
  timeFormat: string;
  resources: Resources;
  changeSet: AccessRuleChangeSet;
  modes: BulkChangeModes;
};

/** One value a bulk edit can change, as a before/after pair of display lines. */
export type ChangeColumn = {
  key: string;
  labelKey: string;
  /** The category whose mode governs this value. */
  modeKey: CategoryKey;
  align: "left" | "center";
  /** Exact width, padding included — the grid is table-layout: fixed. */
  minWidth: number;
  /**
   * Placeholder for an empty value on either side (e.g. Max Days reads
   * "Unlimited"). Both sides resolving to it counts as unchanged.
   */
  emptyLabelKey?: string;
  current: (ctx: CellCtx, rs: LiftAccessRuleSet) => string[];
  /** Lines after the change is applied. */
  resulting: (ctx: CellCtx, rs: LiftAccessRuleSet) => string[];
};

/** Identity fields, in the order the grid's "label: value" card shows them. */
export type IdentityColumn = {
  key: string;
  labelKey: string;
  current: (rs: LiftAccessRuleSet) => string[];
};

export const IDENTITY_COLUMNS: IdentityColumn[] = [
  {
    key: "season",
    labelKey: "LIFT_ACCESS_RULES_TABLE_SEASON",
    current: (rs) => [rs.sellingSeasonDescription],
  },
  {
    key: "fulfillmentSeason",
    labelKey: "LIFT_ACCESS_RULES_TABLE_FULFILLMENT_SEASON",
    current: (rs) => [rs.fulfillmentSeasonDescription],
  },
  {
    key: "id",
    labelKey: "LIFT_ACCESS_RULES_TABLE_ID",
    current: (rs) => [String(rs.entitlementTypeId)],
  },
  {
    key: "description",
    labelKey: "LIFT_ACCESS_RULES_TABLE_DESCRIPTION",
    current: (rs) => [rs.entitlementDescription],
  },
  {
    key: "location",
    labelKey: "LIFT_ACCESS_RULES_TABLE_LOCATION",
    current: (rs) => [rs.locationName],
  },
];

/** Floor for the auto-width identity column. Fits "Fulfillment Season: Winter 2026-2027". */
export const IDENTITY_CARD_WIDTH = 520;

/**
 * The effective rule a bulk edit would leave on this rule set, or null to leave it
 * alone. Every effective column formats from this, never from changeSet.created,
 * which holds only the parts being replaced — a part left unchanged is undefined
 * there and comes from the rule set instead.
 */
const mergedEffective = (c: CellCtx, rs: LiftAccessRuleSet) =>
  mergeEffectiveRule(
    rs.effectiveDateRangeRules?.[0],
    c.changeSet.effectiveDateRange.created[0],
    c.modes.effectiveDate,
    c.modes.effectiveTime,
  );

/**
 * unchanged: current. add: current + defined. replace: defined. clear: nothing.
 *
 * Mirrors buildListCategory. "Show all columns" renders untouched categories too,
 * and their `defined` is empty, so "unchanged" has to answer from `current`.
 */
export const foldList = (current: string[], defined: string[], mode: string) => {
  if (mode === "unchanged") return current;
  if (mode === "clear") return [];
  return mode === "add" ? [...current, ...defined] : defined;
};

/**
 * An "add" skips rules the set already has. Applied here too, so the scan and the
 * request can't disagree about a duplicate add.
 */
const addableRules = <TDefined, TExisting>(
  defined: TDefined[],
  existing: TExisting[] | undefined,
  mode: string,
  getIdentityKey: (rule: TDefined | TExisting) => string,
): TDefined[] =>
  mode === "add" ? rulesToAdd(defined, existing, getIdentityKey) : defined;

export const CHANGE_COLUMNS: ChangeColumn[] = [
  {
    key: "effectiveStart",
    labelKey: "LIFT_ACCESS_RULES_TABLE_EFFECTIVE_DATE",
    modeKey: "effectiveDate",
    align: "center",
    minWidth: 96,
    current: (c, rs) => effectiveStartLines(rs, c.dateFormat),
    resulting: (c, rs) => {
      const merged = mergedEffective(c, rs);
      return merged
        ? cleanLines([formatDateValue(merged.effectiveDateStart, c.dateFormat)])
        : effectiveStartLines(rs, c.dateFormat);
    },
  },
  {
    key: "effectiveEnd",
    labelKey: "LIFT_ACCESS_RULES_TABLE_EXPIRATION_DATE",
    modeKey: "effectiveDate",
    align: "center",
    minWidth: 96,
    current: (c, rs) => effectiveEndLines(rs, c.dateFormat),
    resulting: (c, rs) => {
      const merged = mergedEffective(c, rs);
      return merged
        ? cleanLines([formatDateValue(merged.effectiveDateEnd, c.dateFormat)])
        : effectiveEndLines(rs, c.dateFormat);
    },
  },
  {
    key: "effectiveTime",
    labelKey: "LIFT_ACCESS_RULES_TABLE_EFFECTIVE_TIME",
    modeKey: "effectiveTime",
    align: "center",
    minWidth: 108,
    current: (c, rs) => effectiveTimeLines(rs, c.timeFormat),
    resulting: (c, rs) => {
      const merged = mergedEffective(c, rs);
      return merged
        ? cleanLines([
            timeRangeText(
              merged.effectiveTimeSlotStart,
              merged.effectiveTimeSlotEnd,
              c.timeFormat,
            ),
          ])
        : effectiveTimeLines(rs, c.timeFormat);
    },
  },
  {
    key: "maxDays",
    labelKey: "LIFT_ACCESS_RULES_TABLE_MAX_DAYS",
    modeKey: "maxDays",
    align: "center",
    // Fits the "Max Days" header, which is wider than the values.
    minWidth: 88,
    emptyLabelKey: "ACCESS_RULE_BULK_SUMMARY_UNLIMITED",
    current: (_c, rs) => maxDaysLines(rs),
    // "unchanged" and "clear" answer from the rule set, never from a value left
    // over from a mode the user switched away from.
    resulting: (c, rs) => {
      if (c.modes.maxDays === "unchanged") return maxDaysLines(rs);
      if (c.modes.maxDays === "clear") return [];
      if (c.changeSet.maxDays.created.length === 0) return maxDaysLines(rs);
      return cleanLines(
        c.changeSet.maxDays.created.map((r) =>
          maxDaysLabel(r.numberOfDays, r.sharedUsage),
        ),
      );
    },
  },
  {
    key: "blackoutRange",
    labelKey: "LIFT_ACCESS_RULES_TABLE_BLACKOUT",
    modeKey: "blackoutDateRange",
    align: "left",
    // One "MM/DD/YYYY – MM/DD/YYYY (12 AM – 11:59 PM)" entry per line.
    minWidth: 340,
    current: (c, rs) => blackoutRangeLines(rs, c.dateFormat, c.timeFormat),
    resulting: (c, rs) => {
      const defined = cleanLines(
        addableRules(
          c.changeSet.blackoutDateRange.created,
          rs.blackoutDateRangeRules,
          c.modes.blackoutDateRange,
          blackoutDateRangeDiffAdapter.getIdentityKey,
        ).map((r) => blackoutRangeLabel(r, c.dateFormat, c.timeFormat)),
      );
      const current = blackoutRangeLines(rs, c.dateFormat, c.timeFormat);
      return foldList(current, defined, c.modes.blackoutDateRange);
    },
  },
  {
    key: "blackoutDow",
    labelKey: "LIFT_ACCESS_RULES_TABLE_BLACKOUT_DOW",
    modeKey: "blackoutDayOfWeek",
    align: "left",
    // Worst case on one line: "Su, Mo, Tu, We, Th, Fr, Sa (12 AM – 11:59 PM)".
    minWidth: 300,
    current: (c, rs) => blackoutDowLines(rs, c.resources, c.timeFormat),
    resulting: (c, rs) => {
      const defined = blackoutDowGroupedLabels(
        addableRules(
          c.changeSet.blackoutDayOfWeek.created,
          rs.blackoutDayOfWeekRules,
          c.modes.blackoutDayOfWeek,
          blackoutDayOfWeekDiffAdapter.getIdentityKey,
        ),
        c.resources,
        c.timeFormat,
      );
      const current = blackoutDowLines(rs, c.resources, c.timeFormat);
      return foldList(current, defined, c.modes.blackoutDayOfWeek);
    },
  },
];

/**
 * The most window height worth opening for the grid, so a large selection doesn't
 * ask for one the height of the display. Not a cap on the grid itself, which fills
 * whatever height it's given and scrolls.
 */
export const GRID_HEIGHT_BUDGET = 600;

/**
 * The width the grid wants, for the dialog to size to. Summed from the declared
 * column widths, not measured — so these numbers now drive dialog width too.
 */
export const reviewGridWidthPx = (
  activeCategories: CategoryKey[],
  showAllColumns: boolean,
): number => {
  const changing = new Set(activeCategories);
  const columns = showAllColumns
    ? CHANGE_COLUMNS
    : CHANGE_COLUMNS.filter((col) => changing.has(col.modeKey));
  return (
    IDENTITY_CARD_WIDTH + columns.reduce((total, col) => total + col.minWidth, 0)
  );
};

export type ResolvedCell = {
  beforeText: string;
  afterText: string;
  changed: boolean;
};

/** One value's before/after text for one rule set. */
export function resolveChangedCell(
  col: ChangeColumn,
  ctx: CellCtx,
  rs: LiftAccessRuleSet,
  resources: Resources,
): ResolvedCell {
  const emptyLabel = col.emptyLabelKey
    ? getResource(resources, col.emptyLabelKey)
    : undefined;
  const beforeArr = col.current(ctx, rs);
  const afterArr = col.resulting(ctx, rs);
  const beforeText = beforeArr.length ? beforeArr.join("\n") : emptyLabel ?? "";
  const afterText = afterArr.length ? afterArr.join("\n") : emptyLabel ?? MISSING;
  // Empty on both sides — e.g. "delete all" on a set that had nothing here — isn't
  // a change; flagging it would imply something was removed.
  const changed =
    beforeArr.length === 0 && afterArr.length === 0
      ? false
      : beforeText !== afterText;
  return { beforeText, afterText, changed };
}

export type ScanParams = {
  ruleSets: LiftAccessRuleSet[];
  activeCategories: CategoryKey[];
  changeSet: AccessRuleChangeSet;
  modes: BulkChangeModes;
  resources: Resources;
  dateFormat: string;
  timeFormat: string;
};

export type ScanRow = {
  cells: Record<string, ResolvedCell>;
  anyChanged: boolean;
};

/** A cell line: 12px text at line-height 1.35, rounded up. */
const GRID_LINE_PX = 17;

/** A cell's padding, top and bottom, plus the row separator. */
const GRID_ROW_CHROME_PX = 11;

/** The identity card's padding, border and row gap. */
const IDENTITY_CHROME_PX = 26;
const IDENTITY_GAP_PX = 6;

/** Identity pairs sit two to a grid row. */
const IDENTITY_ROWS = Math.ceil(IDENTITY_COLUMNS.length / 2);

/** The identity card sets a floor under every row. */
const IDENTITY_CARD_HEIGHT =
  IDENTITY_ROWS * GRID_LINE_PX +
  (IDENTITY_ROWS - 1) * IDENTITY_GAP_PX +
  IDENTITY_CHROME_PX;

/** The header row: one line of text. */
const GRID_HEADER_PX = GRID_LINE_PX + GRID_ROW_CHROME_PX;

/** Lines a cell renders. `renderValueLines` splits on newlines. */
const linesIn = (text: string) => (text ? text.split("\n").length : 1);

/**
 * A changed cell counts before plus after: they sit side by side until they don't fit,
 * and which way it goes can't be known without measuring the text.
 */
const rowHeightPx = (row: ScanRow): number => {
  const lines = Object.values(row.cells).map((cell) =>
    cell.changed
      ? linesIn(cell.beforeText) + linesIn(cell.afterText)
      : linesIn(cell.beforeText),
  );
  return (
    Math.max(IDENTITY_CARD_HEIGHT, Math.max(1, ...lines) * GRID_LINE_PX) +
    GRID_ROW_CHROME_PX
  );
};

/**
 * The height the grid wants, for the dialog to size to. Derived from the lines each cell
 * renders, as `reviewGridWidthPx` sums declared widths rather than measuring.
 *
 * Covers only the columns the scan resolved; with every column shown the rest hold
 * current values, shorter than the changes beside them.
 */
export const reviewGridHeightPx = (scan: ChangeScan): number =>
  GRID_HEADER_PX +
  [...scan.rows.values()].reduce((total, row) => total + rowHeightPx(row), 0);

export type ChangeScan = {
  /** Keyed, not positional: a caller rendering a subset must not misalign rows. */
  rows: Map<string, ScanRow>;
  /** Rule sets the edit would actually change — the ones worth saving. */
  changedKeys: Set<string>;
  /** Active categories that change at least one rule set. */
  changedCategories: Set<CategoryKey>;
  /** Selected rule sets the edit would leave alone. */
  unaffectedCount: number;
};

/** Lets the grid resolve a cell on demand for a row the scan missed. */
export const cellCtx = (params: Omit<ScanParams, "ruleSets" | "activeCategories">): CellCtx => ({
  dateFormat: params.dateFormat,
  timeFormat: params.timeFormat,
  resources: params.resources,
  changeSet: params.changeSet,
  modes: params.modes,
});

/**
 * Every changeable value for every selected rule set, in one pass. Formats a value
 * per column per rule set, so don't run it per keystroke or from two callers.
 *
 * INVARIANT: compares displayed values, so every field the mapper persists needs a
 * CHANGE_COLUMNS entry or a real change to it goes undetected.
 */
export function scanBulkEditChanges(params: ScanParams): ChangeScan {
  const {
    ruleSets,
    activeCategories,
    changeSet,
    modes,
    resources,
    dateFormat,
    timeFormat,
  } = params;
  const ctx: CellCtx = {
    dateFormat,
    timeFormat,
    resources,
    changeSet,
    modes,
  };
  const changingCols = CHANGE_COLUMNS.filter((col) =>
    activeCategories.includes(col.modeKey),
  );

  const changedKeys = new Set<string>();
  const changedCategories = new Set<CategoryKey>();
  let unaffectedCount = 0;
  const rows = new Map<string, ScanRow>();

  for (const rs of ruleSets) {
    const cells: Record<string, ResolvedCell> = {};
    let anyChanged = false;

    for (const col of changingCols) {
      const cell = resolveChangedCell(col, ctx, rs, resources);
      cells[col.key] = cell;
      if (cell.changed) {
        anyChanged = true;
        changedCategories.add(col.modeKey);
      }
    }

    const key = liftAccessRuleSetKey(rs);
    if (anyChanged) changedKeys.add(key);
    else if (changingCols.length > 0) unaffectedCount += 1;

    rows.set(key, { cells, anyChanged });
  }

  return { rows, changedKeys, changedCategories, unaffectedCount };
}
